import { useState, useEffect } from "react";
import { getConfig } from "../services/gridApi";

export default function ApiCredentials() {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [config, setConfig] = useState<any>(null);
  const [configError, setConfigError] = useState("");

  const creds = JSON.parse(
    sessionStorage.getItem("grid-credentials") || "{}"
  );

  useEffect(() => {
    getConfig()
      .then(setConfig)
      .catch((e) => setConfigError(e.message));
  }, []);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const authHeader = creds.apiTokenId && creds.apiClientSecret
    ? btoa(`${creds.apiTokenId}:${creds.apiClientSecret}`)
    : "";

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">
          API Credentials
        </h1>
        <p className="text-sm text-[#8A8A8A] mt-1">
          Your Grid API sandbox authentication details
        </p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Current Credentials */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">
            Active Credentials
          </h2>
          <p className="text-sm text-[#8A8A8A] mb-5">
            Currently connected to Grid API sandbox
          </p>

          <div className="space-y-4">
            <div>
              <div className="text-xs text-[#8A8A8A] mb-1">API Token ID</div>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-[#F8F8F7] bg-black/50 px-3 py-1.5 rounded flex-1 truncate">
                  {creds.apiTokenId || "Not set"}
                </code>
                <button
                  onClick={() => handleCopy(creds.apiTokenId, "tokenId")}
                  className="text-xs text-[#8A8A8A] hover:text-[#F8F8F7] px-3 py-1.5 border border-[#333333] rounded hover:bg-[#222222]"
                >
                  {copiedField === "tokenId" ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>

            <div>
              <div className="text-xs text-[#8A8A8A] mb-1">
                API Client Secret
              </div>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-[#F8F8F7] bg-black/50 px-3 py-1.5 rounded flex-1 truncate">
                  {creds.apiClientSecret
                    ? `${creds.apiClientSecret.slice(0, 8)}${"*".repeat(20)}`
                    : "Not set"}
                </code>
                <button
                  onClick={() =>
                    handleCopy(creds.apiClientSecret, "secret")
                  }
                  className="text-xs text-[#8A8A8A] hover:text-[#F8F8F7] px-3 py-1.5 border border-[#333333] rounded hover:bg-[#222222]"
                >
                  {copiedField === "secret" ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>

            <div>
              <div className="text-xs text-[#8A8A8A] mb-1">
                Authorization Header
              </div>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-[#00B3E0] bg-black/50 px-3 py-1.5 rounded flex-1 truncate">
                  Basic {authHeader.slice(0, 20)}...
                </code>
                <button
                  onClick={() =>
                    handleCopy(`Basic ${authHeader}`, "auth")
                  }
                  className="text-xs text-[#8A8A8A] hover:text-[#F8F8F7] px-3 py-1.5 border border-[#333333] rounded hover:bg-[#222222]"
                >
                  {copiedField === "auth" ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Config from API */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">
            Platform Configuration
          </h2>
          <p className="text-sm text-[#8A8A8A] mb-5">
            Live data from GET /config
          </p>

          {configError ? (
            <div className="text-sm text-red-400">{configError}</div>
          ) : config ? (
            <pre className="text-xs font-mono text-[#CCCCCC] bg-black/50 p-4 rounded-lg overflow-auto max-h-96">
              {JSON.stringify(config, null, 2)}
            </pre>
          ) : (
            <div className="text-sm text-[#8A8A8A]">Loading config...</div>
          )}
        </div>

        {/* cURL Example */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">
            Quick Test
          </h2>
          <p className="text-sm text-[#8A8A8A] mb-4">
            Test your credentials with cURL
          </p>

          <div className="relative">
            <pre className="text-xs font-mono text-[#CCCCCC] bg-black/50 p-4 rounded-lg overflow-x-auto">
{`curl -u "${creds.apiTokenId}:${creds.apiClientSecret}" \\
  https://api.lightspark.com/grid/2025-10-13/customers?limit=1`}
            </pre>
            <button
              onClick={() =>
                handleCopy(
                  `curl -u "${creds.apiTokenId}:${creds.apiClientSecret}" https://api.lightspark.com/grid/2025-10-13/customers?limit=1`,
                  "curl"
                )
              }
              className="absolute top-2 right-2 text-xs text-[#8A8A8A] hover:text-[#F8F8F7] px-2 py-1 border border-[#333333] rounded hover:bg-[#222222]"
            >
              {copiedField === "curl" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        {/* Base URL */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">
            API Connection
          </h2>
          <div className="space-y-3 mt-4">
            <div>
              <div className="text-xs text-[#8A8A8A] mb-1">Base URL</div>
              <code className="text-sm font-mono text-[#00B3E0] bg-black/50 px-3 py-1.5 rounded inline-block">
                https://api.lightspark.com/grid/2025-10-13
              </code>
            </div>
            <div>
              <div className="text-xs text-[#8A8A8A] mb-1">Environment</div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-sm text-[#F8F8F7] font-medium">
                  Sandbox
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
